// Детали результата — то, что за кнопкой «ещё». SMY-110
//
// Кнопка на экранах результата была заглушкой, а тогл «Показывать, кто
// выполнил задачу» обещал показать модель «в деталях результата», которых не
// существовало. Здесь и то, и другое становится правдой.
//
// Саму кнопку рисует шапка карточки (`cards/common.tsx`): карточек в ленте
// несколько, и одна кнопка на весь экран открывала бы детали не того
// результата, на который человек смотрит.
//
// Имя модели приходит с сервера, только если человек его попросил тоглом.
// Это не украшение проверки: правило продукта — не называть модели без
// спроса, — и держится оно там, где его нельзя обойти. Клиенту остаётся
// объяснить пустоту, а не додумывать её.

import { useEffect, useState } from 'react'
import { pluralize, type UnitLabel } from '@smyslika/core'
import type { RunDetails } from '@smyslika/api-client'

import { useSession } from '../session.tsx'
import { Caps, Sheet } from '../ui/index.ts'

const SECOND: UnitLabel = { one: 'секунда', few: 'секунды', many: 'секунд' }

/** Сколько заняло. Меньше секунды — «меньше секунды», а не «0 секунд». */
function took(ms: number | null): string {
  if (ms == null) return ''
  if (ms < 1000) return 'меньше секунды'
  const seconds = Math.round(ms / 1000)
  return `${seconds} ${pluralize(seconds, SECOND)}`
}

const WHEN = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
})

export function Details({ runId, open, onClose }: { runId: string; open: boolean; onClose: () => void }) {
  const { runs } = useSession()
  const [data, setData] = useState<RunDetails | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let alive = true
    setError(null)
    runs.details(runId).then(
      (d) => alive && setData(d),
      (e: Error) => alive && setError(e.message),
    )
    return () => {
      alive = false
    }
  }, [open, runId, runs])

  // Тогл включён — значит, приехали поля прозрачности. Спрашивать настройку
  // вторым запросом незачем: ответ сервера и есть ответ на неё.
  //
  // Признак берётся по `externalSources`, а не по имени модели: там, где
  // договор поставщика имя называть не разрешает, модели не будет и при
  // включённом тогле — и подсказка «включи тогл» появилась бы у человека,
  // который его уже включил.
  const named = data?.externalSources != null

  return (
    <Sheet open={open} onClose={onClose} label="Детали результата" closable>
      <h2 className="confirm__title">Детали результата</h2>

      {error != null ? (
        <p className="error-line" role="alert">
          {error}
        </p>
      ) : data == null ? (
        <p className="muted">Смотрю, что делалось…</p>
      ) : (
        <>
          {data.finishedAt != null ? (
            <p className="confirm__explain">Готово {WHEN.format(new Date(data.finishedAt))}</p>
          ) : null}

          <Caps>Что делалось</Caps>
          <div className="details__steps">
            {data.steps.map((s, i) => (
              // Ключ по позиции: этапы приходят по порядку и не переставляются,
              // а одинаковые ярлыки у ретрая — обычное дело.
              <div key={`${s.label}-${i}`} className="details__step">
                <span className="details__label">{s.label}</span>
                <span className="details__meta">
                  {[
                    took(s.durationMs),
                    // Имя модели, если договор поставщика позволяет его
                    // называть; иначе — нейтральное «российский поставщик»,
                    // которое приезжает в `provider`. Оба поля разом не
                    // показываем: «yandex · yandex/flash» это одно и то же
                    // дважды (SMY-79).
                    s.model ?? s.provider,
                    s.region,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            ))}
            {data.steps.length === 0 ? <p className="muted">Этапы не записались.</p> : null}
          </div>

          {/* Три факта, которые обещает тогл сверх имени модели: где
              обрабатывались данные (видно у каждого этапа выше), брались ли
              внешние источники и была ли независимая проверка. Последние два —
              про весь запуск, а не про этап. */}
          {data.externalSources != null ? (
            <>
              <Caps>Как проверялось</Caps>
              <ul className="rules__list">
                <li>
                  {data.externalSources
                    ? 'Ответ опирается на источники из интернета — они перечислены в результате'
                    : 'Внешние источники не использовались: ответ собран без обращения в интернет'}
                </li>
                <li>
                  {data.independentPass === true
                    ? 'Результат проверил второй, независимый поставщик — не тот, что его выдал'
                    : 'Независимой проверки не было: этот сценарий её не делает'}
                </li>
              </ul>
            </>
          ) : null}

          {/* Пустота объясняется, а не оставляется пустотой: человек открыл
              детали как раз затем, чтобы узнать, кто выполнил задачу. */}
          {!named ? (
            <p className="account__fine">
              Кто выполнил — не показано. Включи «Показывать, кто выполнил задачу» в профиле, и модель появится
              здесь у каждого этапа.
            </p>
          ) : null}
        </>
      )}

      <div className="confirm__actions">
        <button type="button" className="btn btn--primary" onClick={onClose}>
          Понятно
        </button>
      </div>
    </Sheet>
  )
}

